import { Component, Output, EventEmitter, OnInit, Input, Inject } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { BaseOption } from '@shared/models/base-option';
import { Observable } from 'rxjs';
import { AboutMeData, User } from '@core/modules/user/models';
import { UserConfig, USER_CONFIG } from '../../user.config';
import { first } from 'rxjs/operators';
import moment from 'moment';

@Component({
    selector: 'app-about-me-form',
    templateUrl: './about-me-form.component.html',
    styleUrls: ['./about-me-form.component.scss'],
    standalone: false
})
export class AboutMeFormComponent implements OnInit {
  @Input() user!: Observable<User | undefined>;
  @Input() navigationBackButton = false;
  @Input() label!: string;
  @Input() professionOptions!: Observable<BaseOption[]>;
  @Input() genderOptions!: Observable<BaseOption[]>;

  @Output() aboutMeSubmit = new EventEmitter<AboutMeData>();
  @Output() backCurrent = new EventEmitter<void>();

  aboutMeForm!: UntypedFormGroup;
  errorFeedbackList = this.loadFeedbackErrorLists();
  isSubmited = false;
  maxDate = moment().subtract(16, 'years').toDate();
  minDate = new Date('1900-01-01');

  constructor(private fb: UntypedFormBuilder, @Inject(USER_CONFIG) private config: UserConfig) {}

  get birthdateControl(): UntypedFormControl {
    return this.aboutMeForm.get('birthdate') as UntypedFormControl;
  }

  get genderControl(): UntypedFormControl {
    return this.aboutMeForm.get('genderId') as UntypedFormControl;
  }

  get professionControl(): UntypedFormControl {
    return this.aboutMeForm.get('professionId') as UntypedFormControl;
  }

  ngOnInit(): void {
    this.errorFeedbackList = this.loadFeedbackErrorLists();
    this.user.pipe(first()).subscribe((user) => (this.aboutMeForm = this.initForm(user)));
  }

  onSubmit() {
    this.isSubmited = true;
    this.aboutMeForm.valid && this.aboutMeSubmit.emit(this.aboutMeForm.value);
  }

  onBack(): void {
    this.backCurrent.emit();
  }

  private initForm(user: User | undefined): UntypedFormGroup {
    return (this.aboutMeForm = this.fb.group({
      birthdate: [user?.birthdate, [this.minDateValidator(), this.maxDateValidator()]],
      genderId: user?.genderId || '',
      professionId: [user?.professionId || '', [Validators.required]]
    }));
  }

  private loadFeedbackErrorLists(): {
    [controlKey: string]: { required: string };
  } {
    return {
      birthdate: this.config.ERRORS.REQUIRED_FIELD,
      gender: this.config.ERRORS.REQUIRED_FIELD,
      profession: this.config.ERRORS.REQUIRED_FIELD
    };
  }

  private minDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value >= this.minDate ? null : { minDate: true };
    };
  }

  private maxDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value <= this.maxDate ? null : { maxDate: true };
    };
  }
}
